/**
 * Column 3: Driver detail - current bucket and allocated trips.
 */

import React from 'react';
import { Zap, AlertCircle, Clock } from 'lucide-react';

function formatTime(isoString, timezone = 'America/Los_Angeles') {
  if (!isoString) return '–';
  try {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return isoString;
  }
}

function formatDateTime(isoString, timezone = 'America/Los_Angeles') {
  if (!isoString) return '–';
  try {
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return isoString;
  }
}

export default function DriverDetailView({ driver, timezone, feasibilityResult, tempTrips, selectedDriverId, selectedTripId }) {
  if (!driver) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 text-[10px] font-bold uppercase tracking-widest py-20 opacity-40">
        <div className="bg-gray-100 p-4 rounded-full mb-4">
          <Zap className="w-10 h-10 text-gray-400" />
        </div>
        <p>Select a driver to audit capacity</p>
      </div>
    );
  }

  const trips = (driver.trips || []).filter((t) => !t.is_break).sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
  const name = driver.driver_name || driver.displayName || driver.driver_id || 'Driver';

  const driverId = String(driver.driver_id || driver.id || '');
  const isRejectedForSelectedTrip =
    selectedTripId &&
    selectedDriverId &&
    driverId === selectedDriverId &&
    (feasibilityResult?.rejected?.get(selectedTripId)?.has(selectedDriverId) ?? false);
  const rejectionReason =
    isRejectedForSelectedTrip && feasibilityResult?.rejected?.get(selectedTripId)?.get(selectedDriverId);

  const feasibleTempTrips = (tempTrips || [])
    .filter((t) => {
      const tripId = t._id || t.id;
      const driverIds = feasibilityResult?.feasible?.get(tripId) || [];
      return tripId && selectedDriverId && driverIds.includes(selectedDriverId);
    });

  const allTripsInBucket = [
    ...trips.map((t, idx) => ({ ...t, _isTemp: false, _displaySeq: idx + 1 })),
    ...feasibleTempTrips.map((t) => ({ ...t, _isTemp: true, _displaySeq: null })),
  ].sort((a, b) => {
    const ta = new Date(a.schedule_time || a.scheduleTime || a.appointment_time || a.appointmentTime || 0).getTime();
    const tb = new Date(b.schedule_time || b.scheduleTime || b.appointment_time || b.appointmentTime || 0).getTime();
    return ta - tb;
  });

  return (
    <div className="p-6 space-y-6">
      {rejectionReason && (
        <div className="rounded-lg border border-red-100 bg-red-50 p-4 shadow-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] font-black text-red-600 uppercase tracking-tight">Audit Violation Identified</div>
            <div className="mt-1 text-xs text-red-700 font-medium leading-relaxed">{rejectionReason}</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-white shadow-sm bg-gray-100">
          {driver.profileImageURL ? (
            <img src={driver.profileImageURL} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm font-black text-gray-400 bg-gray-50 uppercase">
              {(name || 'D')[0]}
            </div>
          )}
        </div>
        <div>
          <div className="text-sm font-black text-gray-900 leading-tight uppercase tracking-tight group-hover:text-main-primary transition-colors">{name}</div>
          <div className="mt-1 flex items-center gap-2">
            {Array.isArray(driver.fleet_windows) && driver.fleet_windows.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                {driver.fleet_windows.map((window) => (
                  <div
                    key={`${window.window_index}-${window.fleet_id || window.rig_no}`}
                    className="flex items-center text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded uppercase tracking-tighter"
                  >
                    <Clock className="w-2.5 h-2.5 mr-1" />
                    {`Rig ${window.rig_no || window.fleet_id || 'Fleet'} ${formatTime(window.start, timezone)} - ${formatTime(window.end, timezone)}`}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded uppercase tracking-tighter">
                <Clock className="w-2.5 h-2.5 mr-1" />
                {driver.shift_window?.shift_start && driver.shift_window?.shift_end
                  ? `${formatTime(driver.shift_window.shift_start, timezone)} - ${formatTime(driver.shift_window.shift_end, timezone)}`
                  : 'Shift N/A'}
              </div>
            )}
            {driver.driver_status && (
              <div className="text-[10px] font-bold px-2 py-0.5 rounded bg-green-50 text-green-700 uppercase tracking-tighter border border-green-100">
                Online
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Audited Bucket Timeline ({allTripsInBucket.length})</h3>
          <div className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase tracking-tighter">Capacity View</div>
        </div>
        
        {allTripsInBucket.length === 0 ? (
          <div className="text-xs text-gray-400 font-bold uppercase italic py-8 text-center opacity-50">No activity registered in this window</div>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {allTripsInBucket.map((trip, idx) => {
              const isTemp = trip._isTemp;
              const tripId = trip.trip_id || trip.tripId || trip.id || trip._id || '–';
              const clientName = trip.client_name || trip.priorityClient?.displayName || '–';
              const schedule = trip.schedule_time || trip.scheduleTime;
              const appointment = trip.appointment_time || trip.appointmentTime;
              
              return (
                <div key={isTemp ? `temp-${trip._id || trip.id}` : trip.id || trip.trip_id || idx} 
                     className={`group relative flex items-center p-3 rounded-lg border transition-all ${
                       isTemp 
                       ? 'bg-indigo-50/30 border-indigo-200' 
                       : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-sm'
                     }`}>
                  <div className={`w-1 self-stretch rounded-full mr-4 ${isTemp ? 'bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]' : 'bg-gray-200'}`} />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-[10px] font-black uppercase tracking-tight ${isTemp ? 'text-indigo-700' : 'text-gray-900 group-hover:text-main-primary transition-colors'}`}>
                          {tripId}
                        </span>
                        {isTemp && (
                          <span className="px-1.5 py-0.5 rounded-[3px] text-[8px] font-black bg-indigo-600 text-white uppercase tracking-widest animate-pulse">Audit Temp</span>
                        )}
                      </div>
                      <span className={`text-[10px] font-black uppercase tracking-widest ${isTemp ? 'text-indigo-400' : 'text-gray-400 opacity-60'}`}>
                        {isTemp ? 'FEASIBLE' : `#${trip._displaySeq ?? trip.sequence ?? idx + 1}`}
                      </span>
                    </div>
                    
                    <div className="text-xs font-bold text-gray-700 truncate">{clientName}</div>
                    
                    <div className="mt-2 flex items-center gap-4">
                      <div className="flex items-center gap-1.5 text-[9px] font-bold text-gray-500 uppercase tracking-tight">
                        <span className="text-gray-400">P/U</span>
                        <span className={isTemp ? 'text-indigo-600' : 'text-gray-700'}>{formatTime(schedule, timezone)}</span>
                      </div>
                      <div className="w-px h-2 bg-gray-200" />
                      <div className="flex items-center gap-1.5 text-[9px] font-bold text-gray-500 uppercase tracking-tight">
                        <span className="text-gray-400">APT</span>
                        <span className={isTemp ? 'text-indigo-600' : 'text-gray-700'}>{formatTime(appointment, timezone)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
